import { AxiosResponseHeaders } from "axios";
import { IApiClient } from "../api-client";
import { EnvType } from "../services";
import { DbDeliveryMetadataExportInfos } from "../Types/db-delivery-metadata-export-infos";
import { DeliveryMetadataExportOrigin } from "../Types/delivery-metadata-export-origin";
import { ExportOrderDataResponse } from "../Types/export-order-data-response";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetMetadataExportResponse } from "../Types/get-metadata-export-response";
import { GetMetadataExportsResponse } from "../Types/get-metadata-exports-response";
import { LiquidValidateAndSaveTemplateRequest } from "../Types/liquid-validate-and-save-template-request";

export interface IDataApiClient {
  GetMetadataExports(): Promise<GetMetadataExportsResponse>;
  GetAssetsMetadataExports(): Promise<GetMetadataExportsResponse>;
  GetMetadataExport(id: number): Promise<GetMetadataExportResponse>;
  CreateMetadataExport(request: DbDeliveryMetadataExportInfos): Promise<GenericResponseStd>;
  UpdateMetadataExport(request: DbDeliveryMetadataExportInfos): Promise<GenericResponseStd>;
  DeleteMetadataExport(id: number): Promise<GenericResponseStd>;
  ExportOrderData(itemId: number): Promise<ExportOrderDataResponse>;
  RenderTemplate(
    templateId: number,
    resourceId: number,
    resourceType: DeliveryMetadataExportOrigin,
    template: string
  ): Promise<ExportOrderDataResponse>;
  LiquidValidateAndSaveTemplate(request: LiquidValidateAndSaveTemplateRequest): Promise<GenericResponseStd>;
  ExportAssetByTemplate(templateId: number, assetId: number): Promise<[string, AxiosResponseHeaders]>;
  GetApiUrl(): string;
}

export default class DataApiClient implements IDataApiClient {
  readonly servicePort = 9523;
  readonly serviceBaseUrl = "data-service/api";

  dataApiClient: IApiClient;

  constructor(dataApiClient: IApiClient, envType: EnvType) {
    this.dataApiClient = dataApiClient;
    if (envType === "local") this.dataApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  async GetMetadataExports(): Promise<GetMetadataExportsResponse> {
    return await this.dataApiClient.get(`${this.serviceBaseUrl}/metadata-exports`);
  }
  async GetAssetsMetadataExports(): Promise<GetMetadataExportsResponse> {
    return await this.dataApiClient.get(`${this.serviceBaseUrl}/metadata-exports/asset-origin`);
  }
  async GetMetadataExport(id: number): Promise<GetMetadataExportResponse> {
    return await this.dataApiClient.get(`${this.serviceBaseUrl}/metadata-exports/${id}`);
  }
  async CreateMetadataExport(request: DbDeliveryMetadataExportInfos): Promise<GenericResponseStd> {
    return await this.dataApiClient.post(`${this.serviceBaseUrl}/metadata-exports`, request);
  }
  async UpdateMetadataExport(request: DbDeliveryMetadataExportInfos): Promise<GenericResponseStd> {
    return await this.dataApiClient.put(`${this.serviceBaseUrl}/metadata-exports`, request);
  }
  async DeleteMetadataExport(id: number): Promise<GenericResponseStd> {
    return await this.dataApiClient.delete(`${this.serviceBaseUrl}/metadata-exports/${id}`);
  }
  async ExportOrderData(itemId: number): Promise<ExportOrderDataResponse> {
    return await this.dataApiClient.get(`${this.serviceBaseUrl}/export-data/order/${itemId}`);
  }
  async RenderTemplate(
    templateId: number,
    resourceId: number,
    resourceType: DeliveryMetadataExportOrigin,
    template: string
  ): Promise<ExportOrderDataResponse> {
    return await this.dataApiClient.post(`${this.serviceBaseUrl}/liquid/render`, {
      templateId: templateId,
      resourceId: resourceId,
      resourceType: resourceType,
      template: template,
    });
  }
  async LiquidValidateAndSaveTemplate(request: LiquidValidateAndSaveTemplateRequest): Promise<GenericResponseStd> {
    return await this.dataApiClient.post(`${this.serviceBaseUrl}/liquid/validate-save`, request);
  }
  async ExportAssetByTemplate(templateId: number, assetId: number): Promise<[string, AxiosResponseHeaders]> {
    return await this.dataApiClient.getWithHeaders(
      `${this.serviceBaseUrl}/export/${templateId}/asset/${assetId}/`,
      "arraybuffer"
    );
  }

  //------Utils------
  GetApiUrl(): string {
    return this.dataApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
